import { create } from 'zustand'
import { recommendationsService, Recommendation } from '@/services/recommendationsService'

interface RecommendationsState {
  recommendations: Recommendation[]
  isLoading: boolean
  error: string | null
  hasFetched: boolean
  
  // Actions
  fetchRecommendations: (email: string, limit?: number, force?: boolean) => Promise<void>
  clearRecommendations: () => void
  setLoading: (loading: boolean) => void
}

export const useRecommendationsStore = create<RecommendationsState>((set, get) => ({
  recommendations: [],
  isLoading: false,
  error: null,
  hasFetched: false,

  fetchRecommendations: async (email: string, limit: number = 10, force: boolean = false) => {
    const state = get()
    
    // Skip fetch if already fetched and not forcing
    if (state.hasFetched && !force) {
      return
    }

    try {
      set({ isLoading: true, error: null })
      const response = await recommendationsService.getRecommendations(email, limit)
      // Deduplicate by title — keep the first (highest-scored) occurrence of each course
      const recs = Array.isArray(response.recommendations) ? response.recommendations : []
      const seenTitles = new Set<string>()
      const seenIds = new Set<number>()
      const unique = recs.filter(r => {
        const titleKey = r.title?.toLowerCase().trim() ?? ''
        if (seenIds.has(r.sessionId) || seenTitles.has(titleKey)) return false
        seenIds.add(r.sessionId)
        seenTitles.add(titleKey)
        return true
      })
      set({ 
        recommendations: unique,
        isLoading: false,
        hasFetched: true
      })
    } catch (error) {
      set({ 
        recommendations: [],
        error: error instanceof Error ? error.message : 'Failed to fetch recommendations',
        isLoading: false,
        hasFetched: true // Mark as fetched even on error to prevent retry loops
      })
    }
  },

  clearRecommendations: () => {
    set({ recommendations: [], error: null, hasFetched: false })
  },

  setLoading: (isLoading: boolean) => {
    set({ isLoading })
  },
}))

